import { useMutation } from "src/Hooks/useReactQuery";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import routeGuard from "src/HOC/routeGuard";
import FormFlightSimulator, { FORM_NAME } from "src/components/FlightSimulator/form";
import APP_ROUTES from "src/routes/routes";
import { createSimulator, editSimulator, getSimulatorsDetail } from "src/services/simulator-flight";
import { EXECUTION_FILE_TYPE, FORM_TYPE, PROCESS_STATUS, ROLES, SIMULATION_TYPE, TARGET_MODE } from "src/constant/common";
import Loading from "src/components/Common/Loading";
import { useSetRecoilState } from "recoil";
import { alertState } from "src/recoil/atoms/global";
import { usePatchedNavigate } from "src/Hooks/usePatchedNavigate";
import { RESOURCE_TYPE } from "src/constant/common";
import flatten from "lodash/flatten";

const FlightSimulatorDetail = () => {
  const navigate = usePatchedNavigate();
  const [errorMessages, setErrorMessages] = useState([]);
  const { t } = useTranslation("flight-simulator");
  const showAlert = useSetRecoilState(alertState);

  const { id } = useParams();
  let [searchParams] = useSearchParams();
  const formAction = searchParams.get("action");
  const page = searchParams.get("page");
  const condition = searchParams.get("condition");
  const type = searchParams.get("type");
  const version_name = searchParams.get("version_name");
  const execution_file_name = searchParams.get("execution_file_name");
  const user_ids = searchParams.get("user_ids");
  const tag_ids = searchParams.get("tag_ids");

  const getRedirectURL = useCallback(
    () =>
      `${APP_ROUTES.HOME}?id=${id}${page ? "&page=" + page : ""}${condition ? "&condition=" + condition : ""}${
        type ? "&type=" + type : ""
      }${version_name ? "&version_name=" + version_name : ""}${execution_file_name ? "&execution_file_name=" + execution_file_name : ""}${
        user_ids ? "&user_ids=" + user_ids : ""
      }${tag_ids ? "&tag_ids=" + tag_ids : ""}`,
    [id, page, condition, type, version_name, execution_file_name, user_ids, tag_ids]
  );

  const { data, mutate: getSimulatorDetailMutate } = useMutation({
    mutationFn: () => getSimulatorsDetail(id),
    onSuccess: (data) => {
      if (data?.status === PROCESS_STATUS.SUCCEEDED && formAction !== FORM_TYPE.COPY) {
        return navigate(APP_ROUTES.NOT_FOUND);
      }
      if (data?.status === PROCESS_STATUS.FAILED && formAction !== FORM_TYPE.EDIT) {
        return navigate(APP_ROUTES.NOT_FOUND);
      }
    },
    onError: (error) => {
      if (error?.response?.status === 404) {
        return navigate(APP_ROUTES.NOT_FOUND);
      }
    },
  });

  const createSimulatorMutate = useMutation({
    mutationFn: ({ data }) => createSimulator({ data }),
  });

  const editSimulatorMutate = useMutation({
    mutationFn: ({ data }) => editSimulator({ data }),
  });

  const showErrorDetailMessage = (errorDetail, alertMessage = "") => {
    if (errorDetail && Array.isArray(errorDetail)) {
      setErrorMessages(errorDetail);

      showAlert({
        status: true,
        message: alertMessage,
        type: "danger",
      });
    }
  };

  const submitData = ({ data }, callback) => {
    if (formAction === FORM_TYPE.EDIT) {
      data.id = id;
      return editSimulatorMutate.mutate(
        { data },
        {
          onSuccess: () => {
            showAlert({
              status: true,
              message: t("new.retry_success"),
              type: "success",
            });
            navigate(
              `${APP_ROUTES.HOME}?id=${id}${page ? "&page=" + page : ""}${condition ? "&condition=" + condition : ""}${
                type ? "&type=" + type : ""
              }${version_name ? "&version_name=" + version_name : ""}${
                execution_file_name ? "&execution_file_name=" + execution_file_name : ""
              }${user_ids ? "&user_ids=" + user_ids : ""}${tag_ids ? "&tag_ids=" + tag_ids : ""}`
            );
          },
          onError: (error) => {
            showErrorDetailMessage(error?.response?.data?.error?.detail, t("new.retry_failed"));
          },
          onSettled: () => callback(),
        }
      );
    }
    if (data.type === SIMULATION_TYPE["3DOF"]) {
      data.series = data.series.map((item) => {
        delete item?.dashboard_urls;
        delete item?.sensitivity_analysis_dashboard_urls;
        return item;
      });
    }
    return createSimulatorMutate.mutate(
      { data },
      {
        onSuccess: () => {
          showAlert({
            status: true,
            message: t("new.create_success"),
            type: "success",
          });
          navigate(APP_ROUTES.HOME);
        },
        onError: (error) => {
          showErrorDetailMessage(error?.response?.data?.error?.detail, t("new.create_failed"));
        },
        onSettled: () => callback(),
      }
    );
  };

  useEffect(() => {
    getSimulatorDetailMutate();
  }, []);

  const transformDetailData = (data) => {
    data[FORM_NAME.PARAMS] = data?.series?.params || [];
    data[FORM_NAME.INPUTS] = data?.series?.inputs || [];
    data[FORM_NAME.FILE_RESPONSE] = data?.series?.response || "";
    if (data?.type === SIMULATION_TYPE["6DOF_MCS"]) {
      data.type = SIMULATION_TYPE["6DOF"];
    }
    if (data?.type === SIMULATION_TYPE["3DOF"]) {
      return {
        ...data,
        series: flatten(
          data?.series?.map((item) => {
            const seriesData = {
              aero_coefficients: item?.aero_files || null,
              aero_coefficients_1: item?.aero1_file || null,
              aero_coefficients_2: item?.aero2_file || null,
              wind_model: item?.wind_model_file || null,
              thrust_profile: item?.thrust_profile_file || null,
              ...(item?.km_line_files?.length && { km_line_files: item?.km_line_files || [] }),
              ...(item?.reference_trajectory_files?.length && { reference_trajectory_files: item?.reference_trajectory_files || [] }),
            };
            if (item?.serie_file_name?.length > 1) {
              return item?.serie_file_name?.map((serie) => {
                return {
                  ...seriesData,
                  parameter_json: serie || [],
                };
              });
            }
            return {
              ...seriesData,
              parameter_json: item?.serie_file_name?.[0] || [],
            };
          })
        ),
      };
    }
    if (
      data?.type === SIMULATION_TYPE["6DOF"] &&
      (data?.execution_file_name === EXECUTION_FILE_TYPE.MAIN_6DOF ||
        data?.execution_file_name === EXECUTION_FILE_TYPE.MCS_6DOF ||
        data?.execution_file_name === EXECUTION_FILE_TYPE.DISPERSION_6DOF ||
        data?.execution_file_name === EXECUTION_FILE_TYPE.EXPECTED_CASUALTY_6DOF)
    ) {
      const getSeriesParams = (series) => {
        return series?.params?.map((param, index) => {
          return {
            parameter_json: param?.serie_file_name || param || null,
            mass_and_shape_response: param?.mass_shape_file || series?.response || null,
            aero_coefficients_ascent: param?.aero1_ascent_file || null,
            aero_coefficients_ascent_2: param?.aero2_ascent_file || null,
            aero_coefficients_descent: param?.aero_descent_file || null,
            wind_model: param?.wind_model_file || null,
            thrust_profile: param?.thrust_profile_file || null,
            target_mode: param?.is_program_rate_control === TARGET_MODE.ATTITUDE ? TARGET_MODE.ATTITUDE : TARGET_MODE.RATE,
            target_attitude_rate_ascent: param?.target_attitude_rate_ascent_file || null,
            target_attitude_rate_descent: param?.target_attitude_rate_descent_file || null,
            target_attitude_ascent: param?.target_attitude_ascent_file || null,
            target_attitude_descent: param?.target_attitude_descent_file || null,
          };
        });
      };

      return {
        ...data,
        series: getSeriesParams(data?.series),
      };
    }

    return data;
  };

  if (getSimulatorDetailMutate.isLoading) return <Loading />;

  return (
    <>
      {data && (
        <FormFlightSimulator
          submitData={submitData}
          data={transformDetailData(data)}
          title={t("new.title_detail")}
          getRedirectURL={getRedirectURL}
          errorMessages={errorMessages}
        />
      )}
    </>
  );
};

export default routeGuard(FlightSimulatorDetail, RESOURCE_TYPE.FLIGHT_SIMULATION, ROLES.EDITOR);
